import { arktypeResolver } from '@hookform/resolvers/arktype';
import { SxProps } from '@mui/material';
import {
  AiPriseBusinessProfileEvent,
  AiPriseErrorEvent,
  AiPriseSessionEvent,
} from 'aiprise-web-sdk';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { calizaApi, useCreateCustomerKYXVerificationMutation } from '@/api/v1';
import { AiPriseFrame } from '@/components/organisms/AiPriseFrame';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { Header } from '@/components/organisms/Header';
import { useFormWithErrors } from '@/hooks/useFormWithErrors';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { AddCustomerFormData, addCustomerSchema } from '@/schemas/addCustomerSchema';
import { AddCustomerForm } from '@/Screens/Dashboard/Customers/AddCustomerForm';
import { BeneficiaryType } from '@/types/customer';

const HEADER_CONTENT_SX: SxProps = { width: '100%' };

export const AddCustomerModal: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const tr = useTranslator();
  const toast = useToast();
  const dispatch = useDispatch();
  const [showAiPriseFrame, setShowAiPriseFrame] = React.useState<boolean>(false);
  const [createCustomerKYXVerification, { isLoading, data: createdCustomer }] =
    useCreateCustomerKYXVerificationMutation();
  const formMethods = useFormWithErrors<AddCustomerFormData>({
    resolver: arktypeResolver(addCustomerSchema),
    defaultValues: {
      customerType: BeneficiaryType.business,
      businessName: '',
    },
  });
  const { getValues, watch } = formMethods;
  const customerType = watch('customerType');
  const userData = React.useMemo((): { firstName: string; lastName: string } | undefined => {
    const formValues = getValues();

    if (formValues.customerType === BeneficiaryType.individual) {
      return {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
      };
    }

    return undefined;
  }, [getValues]);

  const handleClose = React.useCallback((): void => {
    navigate('..');
  }, [navigate]);

  const handleStartVerification = React.useCallback((): void => {
    setShowAiPriseFrame(true);
  }, []);

  const handleAiPriseStarted = React.useCallback(
    async (event: AiPriseSessionEvent): Promise<void> => {
      console.debug('➡️ AiPrise Event: started', event.detail);
      const sessionId = event.detail.verification_session_id;
      const formValues = getValues();

      try {
        if (formValues.customerType === BeneficiaryType.business) {
          await createCustomerKYXVerification({
            type: BeneficiaryType.business,
            businessName: formValues.businessName,
            sessionId,
          }).unwrap();
        } else {
          await createCustomerKYXVerification({
            type: BeneficiaryType.individual,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            sessionId,
          }).unwrap();
        }

        toast.show(tr('CUSTOMER_VERIFICATION_STARTED_SUCCESS'), 'success');
      } catch (error: unknown) {
        console.error('Error creating customer KYX verification:', error);
        toast.show(tr('CUSTOMER_VERIFICATION_CREATE_ERROR'), 'error');
      }
    },
    [createCustomerKYXVerification, getValues, toast, tr],
  );

  const handleAiPriseError = React.useCallback((event: AiPriseErrorEvent): void => {
    console.error('❌ AiPrise Event: error', event.detail);
  }, []);

  const handleAiPriseBusinessProfile = React.useCallback(
    (event: AiPriseBusinessProfileEvent): void => {
      console.debug('🏢 AiPrise Event: business-profile', event.detail);
    },
    [],
  );

  const handleAiPriseSuccessful = React.useCallback(
    (event: AiPriseSessionEvent): void => {
      console.debug('✅ AiPrise Event: successful', event.detail);
      toast.show(tr('CUSTOMER_VERIFICATION_SUCCESS'), 'success');

      if (createdCustomer?.id) {
        dispatch(
          calizaApi.util.invalidateTags([
            { type: 'CustomerKYXVerifications', id: createdCustomer.id },
            { type: 'CustomerKYXVerifications', id: 'LIST' },
          ]),
        );
      }
    },
    [toast, tr, dispatch, createdCustomer],
  );

  const handleAiPriseContinue = React.useCallback(
    (event: AiPriseSessionEvent): void => {
      console.debug('➡️ AiPrise Event: continue', event.detail);
      handleClose();
    },
    [handleClose],
  );

  return (
    <ApplicationDialog fullScreen={showAiPriseFrame}>
      {!showAiPriseFrame ? (
        <ApplicationModal title={tr('ADD_NEW_CUSTOMER')} onClose={handleClose}>
          <AddCustomerForm
            formMethods={formMethods}
            isLoading={isLoading}
            onSubmit={handleStartVerification}
          />
        </ApplicationModal>
      ) : (
        <>
          <Header title="ADD_NEW_CUSTOMER" onClose={handleClose} contentSx={HEADER_CONTENT_SX} />
          {customerType === BeneficiaryType.business ? (
            <AiPriseFrame
              customerType={BeneficiaryType.business}
              onStarted={handleAiPriseStarted}
              onError={handleAiPriseError}
              onSuccessful={handleAiPriseSuccessful}
              onContinue={handleAiPriseContinue}
              onBusinessProfile={handleAiPriseBusinessProfile}
              fullScreen
            />
          ) : customerType === BeneficiaryType.individual ? (
            <AiPriseFrame
              customerType={BeneficiaryType.individual}
              userData={userData}
              onStarted={handleAiPriseStarted}
              onError={handleAiPriseError}
              onSuccessful={handleAiPriseSuccessful}
              onContinue={handleAiPriseContinue}
              fullScreen
            />
          ) : null}
        </>
      )}
    </ApplicationDialog>
  );
};
